/**
 * Script sampling for the whole-video AI generators — the design theme's art
 * direction (client-side, `features/design`) and the thumbnail hooks
 * (server-side, `ai/actions/thumbnails`). In @kola/shared so both ends cut the
 * SAME sample from the same script: the server's caps and the client's
 * pre-cut can never disagree about what "a sample" is.
 *
 * WHY SAMPLE AT ALL. The Original Script column (F-28) is the primary workload
 * at ~10,000 rows typical and 50,000 max (docs/01). Sending it whole would cost
 * a fortune, blow past the route's caps, and — the part that actually matters —
 * would not produce a better art direction. Art direction is a WHOLE-VIDEO
 * judgement: it needs the subject, the register and the arc, and none of those
 * sharpen past a few thousand characters.
 *
 * WHY BEGINNING + MIDDLE + END, not the first N rows. A script's opening is its
 * hook — atypical by design — and taking only the head would art-direct the
 * hook rather than the video. Even spacing across the middle catches the actual
 * body, and the tail catches how it lands. The prompt tells the model the gaps
 * are sampling artifacts and not scene breaks (see the server's
 * `buildArtDirectionPrompt`), so it does not try to read a jump-cut into them.
 *
 * Pure: the caller already has every row in memory (the grid on the client,
 * the live doc on the server). A route still enforces its own caps on what it
 * receives, so this is a cooperation, not a trust boundary.
 */
/** Lines one sample may carry. Matches the server's `DEFAULT_DESIGN_MAX_SAMPLE_LINES`. */
export const SAMPLE_MAX_LINES = 120;
/** Characters one sample may carry. Matches `DEFAULT_DESIGN_MAX_SAMPLE_CHARS`. */
export const SAMPLE_MAX_CHARS = 12_000;
/**
 * A single line's cap. Long enough for a paragraph-length row, short enough
 * that one pathological cell cannot eat the whole character budget.
 */
export const SAMPLE_MAX_LINE_CHARS = 2_000;
/** Head and tail rows taken verbatim before the middle is spread over the rest. */
export const SAMPLE_EDGE_LINES = 12;
/**
 * Pick a representative sample of `lines`, in script order.
 *
 * Guarantees, in order of precedence:
 *  1. the result is within BOTH caps;
 *  2. the result is in the original order, with no duplicates;
 *  3. a short script is returned WHOLE (no sampling artifact where none is needed);
 *  4. a long script keeps its opening and its ending, and spreads the remaining
 *     budget evenly across the middle.
 *
 * Blank and whitespace-only rows are dropped — the grid is full of them (a
 * script column has gaps), they carry no signal, and each one would otherwise
 * spend a line of the budget.
 */
export function sampleScript(lines, options = {}) {
    const maxLines = options.maxLines ?? SAMPLE_MAX_LINES;
    const maxChars = options.maxChars ?? SAMPLE_MAX_CHARS;
    const clean = [];
    for (const line of lines) {
        const trimmed = line.trim();
        if (trimmed.length === 0)
            continue;
        clean.push(trimmed.slice(0, SAMPLE_MAX_LINE_CHARS));
    }
    if (clean.length === 0)
        return [];
    const picked = clean.length <= maxLines ? clean : pickSpread(clean, maxLines);
    return capChars(picked, maxChars);
}
/**
 * `maxLines` entries from `lines`, in order: the first and last
 * `SAMPLE_EDGE_LINES` verbatim, the rest spread evenly across the middle.
 */
function pickSpread(lines, maxLines) {
    const edge = Math.min(SAMPLE_EDGE_LINES, Math.floor(maxLines / 3));
    const head = lines.slice(0, edge);
    const tail = lines.slice(lines.length - edge);
    const middleBudget = maxLines - head.length - tail.length;
    const middleStart = edge;
    const middleEnd = lines.length - edge;
    const middleCount = middleEnd - middleStart;
    if (middleBudget <= 0 || middleCount <= 0) {
        return [...head, ...tail].slice(0, maxLines);
    }
    const middle = [];
    // Sample at the CENTRE of each of `middleBudget` equal buckets rather than at
    // bucket starts: on a script whose sections are roughly regular, start-of-
    // bucket sampling tends to land on the same structural beat every time (every
    // section's opening line), which reads as a much narrower script than it is.
    const step = middleCount / middleBudget;
    for (let i = 0; i < middleBudget; i++) {
        const index = middleStart + Math.floor((i + 0.5) * step);
        const line = lines[Math.min(index, middleEnd - 1)];
        if (line !== undefined)
            middle.push(line);
    }
    return [...head, ...middle, ...tail];
}
/**
 * Trim from the MIDDLE outward until the total fits `maxChars`, so the opening
 * and ending survive a character-dense script. Never returns an empty array
 * when there was at least one line — a single over-long line is truncated
 * rather than dropped, because "no sample" and "a big sample" should not have
 * the same outcome.
 */
function capChars(lines, maxChars) {
    const total = (list) => list.reduce((sum, line) => sum + line.length, 0);
    const out = [...lines];
    while (out.length > 1 && total(out) > maxChars) {
        out.splice(Math.floor(out.length / 2), 1);
    }
    if (out.length === 1 && (out[0]?.length ?? 0) > maxChars) {
        out[0] = out[0].slice(0, maxChars);
    }
    return out;
}
/** How many opening lines of each short the sample keeps. */
export const SAMPLE_LINES_PER_SHORT = 6;
/**
 * The sample for a WEEK OF SHORTS: each short's opening lines under its own
 * header, in slot order. `sampleScript` cuts beginning, middle and end of one
 * script — run over five stacked scripts it would hand the model the start of
 * Short 1, the middle of Short 3 and the end of Short 5, one and a half
 * topics out of five. A short is a hook and a few beats, so its opening lines
 * ARE the short; five of those, each named, is what an art direction or a
 * set of cover hooks should be written from. Empty shorts are skipped: a
 * header with nothing under it would read as a short with no content, which
 * the model would then invent.
 */
export function sampleShortsScript(shorts, options = {}) {
    const perShort = Math.max(1, options.linesPerShort ?? SAMPLE_LINES_PER_SHORT);
    const maxLineChars = options.maxLineChars ?? SAMPLE_MAX_LINE_CHARS;
    const out = [];
    for (const short of [...shorts].sort((a, b) => a.slot - b.slot)) {
        const lines = short.lines
            .map((line) => line.trim())
            .filter((line) => line.length > 0)
            .slice(0, perShort)
            .map((line) => (line.length > maxLineChars ? `${line.slice(0, maxLineChars - 1)}…` : line));
        if (lines.length === 0)
            continue;
        out.push(`Short ${short.slot}:`);
        out.push(...lines);
    }
    return out;
}
//# sourceMappingURL=script-sample.js.map